import {Percent, Zap, Beaker} from 'lucide-react'

export default function EducationalSection() {
  return (
    <section className="mt-10 mb-12">
      <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8 card-hover">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500 mb-2">
          Baker's Reference
        </p>
        <h2 className="text-2xl font-bold text-gray-900 mb-8">
          Understanding Baker's Math
        </h2>

        <div className="grid gap-8 md:grid-cols-3">
          {/* Block 1 */}
          <div className="space-y-3">
            <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <Percent size={17} className="text-indigo-600" strokeWidth={2.2} />
            </div>
            <h3 className="text-base font-bold text-gray-900">
              How to Calculate Baker's Percentage
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Baker's percentage expresses every ingredient as a ratio of the total flour weight. The formula is:
            </p>
            <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
              <code className="text-xs font-mono text-indigo-700 font-semibold tabular-nums">
                % = (Ingredient Weight ÷ Total Flour Weight) × 100
              </code>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              For example, 650g of water with 1000g of flour yields 65% hydration. This system lets professional bakers scale any recipe proportionally while maintaining identical flavor profiles and textures.
            </p>
          </div>

          {/* Block 2 */}
          <div className="space-y-3">
            <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <Zap size={17} className="text-emerald-600" strokeWidth={2.2} />
            </div>
            <h3 className="text-base font-bold text-gray-900">
              Why Use a Baker's Math Calculator Instead of an Excel Spreadsheet?
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Excel requires manual formula setup, offers no built-in true hydration tracking, and breaks silently with copy-paste errors. This calculator provides:
            </p>
            <ul className="text-sm text-gray-600 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-emerald-500 font-bold mt-0.5">✓</span>
                Bi-directional weight ↔ percentage sync in real time
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-500 font-bold mt-0.5">✓</span>
                True sourdough hydration accounting for hidden starter flour/water
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-500 font-bold mt-0.5">✓</span>
                Geometric pan size scaling with area math
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-500 font-bold mt-0.5">✓</span>
                Zero NaN crashes — robust numeric validation throughout
              </li>
            </ul>
          </div>

          {/* Block 3 */}
          <div className="space-y-3">
            <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <Beaker size={17} className="text-amber-600" strokeWidth={2.2} />
            </div>
            <h3 className="text-base font-bold text-gray-900">
              Converting Cups to Grams in Baking
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Volumetric measurements (cups, tablespoons) are notoriously inaccurate for precision baking. A "cup" of flour can vary between 120g and 170g depending on how it's scooped — a 40% swing that destroys hydration targets.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Professional bakers universally rely on gram weights. This calculator enforces gram-based input, ensuring your baker's percentages remain mathematically precise across every batch, oven, and altitude.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
